import './barcode-subscriber.scss';

import { faBarcode, faCamera, faCircleNotch, faLightbulb, faSyncAlt, faMagic } from '@fortawesome/free-solid-svg-icons';
import { first } from 'rxjs/operators';
import { Component } from 'vue-property-decorator';
import { QuaggaJSCodeReader, QuaggaJSReaderConfig } from '@ericblade/quagga2';

import { ReactiveVue } from '@skinsoft/reactive-vue';

import { input$ } from '../keyboard';
import { CameraScanner } from './CameraScanner';

declare const CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE: boolean;
declare const CAMERA_SCANNER_READERS: (QuaggaJSReaderConfig | QuaggaJSCodeReader)[];

const CAMERA_PERMISSION_NOT_ALLOWED_ERRORS = ["NotAllowedError"]
const CAMERA_NOT_FOUND_ERRORS = ["NotFoundError"]

@Component({
    props: ['width', 'height', 'embedded', 'eventFilterIdentifier'],
    template: require('./barcode-subscriber.html'),
})
export class BarcodeSubscriber extends ReactiveVue {
    public $refs: {
        innerWrapper: HTMLDivElement;
        video: HTMLVideoElement;
    };

    public eventFilterIdentifier: string;
    public width: number;
    public height: number;
    public embedded: boolean;

    private value: string = null as any;
    private hasSeveralDevices: boolean = false;
    private cameraReady: boolean = false;
    private showWarning: boolean = false;
    private warningMessage: string = ''

    // Icons

    private lightBulbIcon = faLightbulb;
    private barcodeIcon = faBarcode;
    private cameraIcon = faCamera;
    private changeIcon = faSyncAlt;
    private loadingIcon = faCircleNotch;
    private magicIcon = faMagic

    get supportsCamera() {
        return (
            CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE &&
            navigator &&
            navigator.mediaDevices
        )
    }

    private cameraScanner: CameraScanner = null as any;

    private torchAvailable: boolean = false;

    public created() {
        if(this.supportsCamera) {
            this.instanciateCameraScanner()
        }
    }

    public async mounted() {
        if (this.supportsCamera) {
            console.info("BarCodeSubscriber: Supports camera")

            console.info("BarCodeSubscriber: Checking camera permission...")
            const cameraPermissionStatus: PermissionState | "UNKNOWN" = await this.getCameraPermissionStatus()
            switch(cameraPermissionStatus) {
                case 'denied':
                    console.info("BarCodeSubscriber: Checking camera permission -> denied")
                    this.warnUser(this.$t("alerts.permissions.camera.permissionDeniedWarning") as string)
                    return
                case 'prompt':
                    console.info("BarCodeSubscriber: Checking camera permission -> prompt")
                    this.warnUser(this.$t("alerts.permissions.camera.askForPermission") as string)
                    break
                case 'granted':
                    console.info("BarCodeSubscriber: Checking camera permission -> granted")
                    break
                case "UNKNOWN":
                    console.info("BarCodeSubscriber: Checking camera permission -> UNKNOWN")
                    // Logger
                    console.warn("Cannot get camera permission status")
                    break
            }

            // no need to call navigator.mediaDevices.getUserMedia, it is done by Quagga
            if(!this.cameraScanner) {
                this.instanciateCameraScanner()
            }
            console.info("BarCodeSubscriber: Starting scanner")
            this.startCameraScanner()
            
        } else {
            console.info("BarCodeSubscriber: Doesn't support camera")
            console.info("Camera scanner is enabled: ", CAMERA_SCANNER_ENABLE_ON_COMPATIBLE_DEVICE)
            this.subscribeToKeyPress();
        }
    }

    private async getCameraPermissionStatus(): Promise<PermissionState | "UNKNOWN"> {
        try {
            // @ts-ignore
            const cameraPermissionStatus: PermissionStatus = await navigator.permissions.query({ name: 'camera' })
            
            if(cameraPermissionStatus?.state) {
                return cameraPermissionStatus.state
            }
            return "UNKNOWN"
        } catch(error) {
            console.info("BarCodeSubscriber: Error while checking camera permission: ", error)
            // Logger
            return "UNKNOWN"
        }
    }

    private instanciateCameraScanner() {
        console.info("BarCodeSubscriber: Instanciating camera scanner...")
        this.cameraScanner = new CameraScanner(
            CAMERA_SCANNER_READERS,
            () => {
                console.info("BarCodeSubscriber: Camera ready")
                this.cameraReady = true;
            },
            () => {
                console.info("BarCodeSubscriber: Camera not ready")
                this.cameraReady = false;
            },
            this.width, 
            this.height,
            '#interactive',
        )    
    }

    private async startCameraScanner() {
        this.cameraScanner.pipe(first()).subscribe(async code => {
            code.split('').forEach(char => input$.next(
                {
                    char,
                    inputEventIdentifier: this.eventFilterIdentifier,
                }
            ));
            await this.close();
        });
        console.info("BarCodeSubscriber: Starting camera scanner...")
        await this.cameraScanner.start();
        // Check if torch is available
        const videoStream = this.$refs.video?.srcObject;
        if (videoStream) {
            //console.info("BarCodeSubscriber: video stream: ", videoStream)
            // @ts-ignore
            const capabilities = videoStream.getTracks()[0].getCapabilities();
            this.torchAvailable = !!capabilities.torch;
        }
        // check if the button to change camera has to be displayed
        this.hasSeveralDevices = this.cameraScanner.hasSeveralDevices;
    }

    public async close() {
        if (this.supportsCamera) {
            console.info("BarCodeSubscriber: Stopping camera...")
            await this.cameraScanner.stop();
            this.$emit('close');
        } else {
            this.unsubscribeFromKeyPress();
        }
    }

    public async beforeDestroy() {
        this.unsubscribeFromKeyPress();
        this.cameraScanner.resetCallbacks()
        // if we don't stop it manually, camera is still in use in the background
        console.info("BarCodeSubscriber: Stopping camera...")
        await this.cameraScanner.stop()
    }

    public toggleTorch() {
        this.cameraScanner.toggleTorch();
    }

    public async restartCameraScanner() {
        await this.cameraScanner.stop()
        this.cameraScanner.start()
    }

    public changeCamera() {
        this.cameraScanner.goToNextCameraDevice();
    }

    private subscribeToKeyPress() {
        window.addEventListener('keypress', this.onKeyPress);
    }

    private unsubscribeFromKeyPress() {
        window.removeEventListener('keypress', this.onKeyPress);
    }

    private onKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();
        input$.next({ char: String.fromCharCode(event.which), inputEventIdentifier: this.eventFilterIdentifier });
    };

    private warnUser(message: string) {
        this.showWarning = true
        this.warningMessage = message
    }
}
